import { useEffect, useMemo, useState } from "react";
import { Trash2, UserPlus, Loader2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { ROLES, ROLE_LABEL, AREAS } from "@/lib/roles";

const field = "w-full rounded-xl border border-[#F4EEE2]/15 bg-[#0B111E] px-3 py-2.5 text-sm text-[#F4EEE2] outline-none focus:border-[#D4AF37]";
const EMPTY = { name: "", email: "", password: "", role: "editor", areas: ["sermons", "announcements", "text"], active: true, send_welcome: true };
const BADGE = { super_admin: "bg-[#D4AF37] text-[#0B111E]", editor: "bg-[#1C2D42] text-[#F4EEE2]", viewer: "bg-[#2a3449] text-[#C9D0DB]" };

const UserForm = ({ initial, me, onClose, onSaved }) => {
  const editing = Boolean(initial?.id);
  const [f, setF] = useState(editing ? { ...initial, password: "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const toggleArea = (a) => setF((p) => ({ ...p, areas: p.areas.includes(a) ? p.areas.filter((x) => x !== a) : [...p.areas, a] }));
  const self = editing && initial.id === me.id;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const body = { name: f.name, role: f.role, areas: f.areas, active: f.active, can_publish: Boolean(f.can_publish) };
        if (f.password) body.password = f.password;
        await api.put(`/users/${initial.id}`, body);
      } else {
        await api.post("/users", { name: f.name, email: f.email, password: f.password, role: f.role, areas: f.areas, can_publish: Boolean(f.can_publish), send_welcome: f.send_welcome });
      }
      toast.success(editing ? "Account updated" : f.send_welcome ? "Account created — welcome email sent" : "Account created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.post(`/users/${initial.id}/welcome`);
      toast.success("Welcome email sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent data-testid="user-form-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-[#D4AF37]/20 bg-[#131C2E] text-[#F4EEE2]">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{editing ? "Edit account" : "New account"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <input data-testid="user-name-input" required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} />
          <input data-testid="user-email-input" required type="email" placeholder="Email" value={f.email} disabled={editing} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${field} disabled:opacity-60`} />
          <input data-testid="user-password-input" required={!editing} minLength={8} type="text" placeholder={editing ? "New password (leave empty to keep)" : "Temporary password (8+ characters)"} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={field} />
          <p className="mt-2 font-ui text-xs uppercase tracking-widest text-[#9AA4B4]">Role</p>
          <div className="grid gap-2">
            {ROLES.map(([k, label, desc]) => (
              <button type="button" key={k} data-testid={`user-role-${k}`} disabled={self} onClick={() => setF({ ...f, role: k })} className={`rounded-xl border px-4 py-3 text-left transition-colors disabled:opacity-60 ${f.role === k ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#F4EEE2]/10 hover:border-[#F4EEE2]/30"}`}>
                <span className="block font-ui text-sm font-semibold">{label}</span>
                <span className="block text-xs text-[#B7BFCC]">{desc}</span>
              </button>
            ))}
          </div>
          {f.role !== "super_admin" && (
            <>
              <p className="mt-2 font-ui text-xs uppercase tracking-widest text-[#9AA4B4]">Areas they can {f.role === "viewer" ? "view" : "view & edit"}</p>
              <div className="grid grid-cols-2 gap-2">
                {AREAS.map(([k, label]) => (
                  <label key={k} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2.5 text-sm ${f.areas.includes(k) ? "border-[#D4AF37]/60 text-[#F4EEE2]" : "border-[#F4EEE2]/10 text-[#B7BFCC]"}`}>
                    <input type="checkbox" data-testid={`user-area-${k}`} checked={f.areas.includes(k)} onChange={() => toggleArea(k)} className="accent-[#D4AF37]" /> {label}
                  </label>
                ))}
              </div>
            </>
          )}
          {f.role === "editor" && (
            <label className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-[#C8862F]/40 px-4 py-3 text-sm">
              <span>Can press PUBLISH<span className="block text-xs text-[#9AA4B4]">Lets this person put website changes live. Also tick the Visual Editor area above. Super Admins can always publish.</span></span>
              <Switch data-testid="user-publish-toggle" checked={Boolean(f.can_publish)} onCheckedChange={(v) => setF({ ...f, can_publish: v, areas: v && !f.areas.includes("design") ? [...f.areas, "design"] : f.areas })} className="shrink-0 data-[state=checked]:bg-[#D4AF37] data-[state=unchecked]:bg-[#3A4558]" />
            </label>
          )}
          {editing && !self && (
            <label className="mt-2 flex items-center justify-between rounded-xl border border-[#F4EEE2]/10 px-4 py-3 text-sm">
              Account active (can sign in)
              <Switch data-testid="user-active-toggle" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} className="data-[state=checked]:bg-[#D4AF37] data-[state=unchecked]:bg-[#3A4558]" />
            </label>
          )}
          {!editing && (
            <label className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-[#F4EEE2]/10 px-4 py-3 text-sm">
              <span>Send a welcome email with their role and the portal link<span className="block text-xs text-[#9AA4B4]">Share the temporary password with them yourself — it is never emailed.</span></span>
              <Switch data-testid="user-welcome-toggle" checked={f.send_welcome} onCheckedChange={(v) => setF({ ...f, send_welcome: v })} className="shrink-0 data-[state=checked]:bg-[#D4AF37] data-[state=unchecked]:bg-[#3A4558]" />
            </label>
          )}
          {editing && (
            <button type="button" data-testid="user-resend-welcome-btn" onClick={resend} className="mt-2 rounded-full border border-[#D4AF37]/40 py-2.5 font-ui text-sm text-[#F4EEE2] hover:bg-[#D4AF37]/10">
              Re-send welcome email
            </button>
          )}
          <button data-testid="user-save-btn" disabled={busy} className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3 font-ui text-sm font-semibold text-[#0B111E] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Create account"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const TeamPanel = ({ me }) => {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const load = () => api.get("/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => setUsers([]));
  useEffect(() => {
    load();
  }, []);

  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return users.filter((u) => (role === "all" || u.role === role) && `${u.name} ${u.email}`.toLowerCase().includes(s));
  }, [users, q, role]);
  const count = (r) => users.filter((u) => u.role === r).length;

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? They will no longer be able to sign in.`)) return;
    await api.delete(`/users/${u.id}`).then(() => { toast.success("Removed"); load(); }).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };

  return (
    <div data-testid="team-panel">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <label className="flex flex-1 items-center gap-2 rounded-full border border-[#F4EEE2]/15 px-4 py-2.5">
          <Search className="h-4 w-4 text-[#B7BFCC]" />
          <input data-testid="team-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="w-full bg-transparent text-sm text-[#F4EEE2] outline-none placeholder:text-[#9AA4B4]" />
        </label>
        <div className="flex flex-wrap gap-1">
          {[["all", `All (${users.length})`], ...ROLES.map(([k, l]) => [k, `${l} (${count(k)})`])].map(([k, l]) => (
            <button key={k} data-testid={`team-filter-${k}`} onClick={() => setRole(k)} className={`rounded-full px-3 py-2 font-ui text-xs ${role === k ? "bg-[#F4EEE2] font-semibold text-[#0B111E]" : "text-[#F4EEE2]/80 hover:bg-[#F4EEE2]/5"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="team-add-btn" onClick={() => setEditing({})} className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 font-ui text-sm font-semibold text-[#0B111E]">
          <UserPlus className="h-4 w-4" /> Add account
        </button>
      </div>

      <div className="mt-6">
        {shown.map((u) => (
          <div key={u.id} data-testid={`team-row-${u.id}`} className={`flex flex-wrap items-center gap-3 border-b border-[#F4EEE2]/10 py-4 ${u.active ? "" : "opacity-60"}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-ui font-semibold text-[#F4EEE2]">{u.name} {u.id === me.id && <span className="text-xs font-normal text-[#B7BFCC]">(you)</span>} {!u.active && <span className="text-xs font-normal text-[#F2A08A]">· turned off</span>}</p>
              <p className="truncate text-sm text-[#B7BFCC]">{u.email}</p>
              {u.role !== "super_admin" && (
                <p className="mt-1 truncate text-xs text-[#9AA4B4]">{AREAS.filter(([k]) => u.areas.includes(k)).map(([, l]) => l).join(" · ") || "No areas assigned"}</p>
              )}
            </div>
            {u.can_publish && <span data-testid={`team-can-publish-${u.id}`} className="rounded-full border border-[#C8862F]/50 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-[#C8862F]">Can publish</span>}
            <span data-testid={`team-role-${u.id}`} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${BADGE[u.role] || BADGE.viewer}`}>{ROLE_LABEL[u.role] || u.role}</span>
            <button data-testid={`team-edit-${u.id}`} onClick={() => setEditing(u)} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
            {u.id !== me.id && (
              <button data-testid={`team-remove-${u.id}`} onClick={() => remove(u)} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
        {!shown.length && <p className="py-8 text-center text-sm text-[#B7BFCC]">No accounts match.</p>}
      </div>

      {editing && <UserForm initial={editing.id ? editing : null} me={me} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
